/**
 * Product request queue — tenants asking to use JobPilot / EvalPilot.
 * Approving starts a 14-day trial and grants the owners access.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Hourglass } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface ProductRequest {
  id: string;
  tenant_id: string;
  product: string;
  status: string;
  requested_at: string | null;
  request_note: string | null;
  company_name: string;
  tenant_code: string;
}

const PRODUCT_LABEL: Record<string, string> = { jobpilot: 'JobPilot', evalpilot: 'EvalPilot' };

export function ProductRequests() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'product-requests'],
    queryFn: () => apiFetch<{ requests: ProductRequest[] }>('/platform/product-requests'),
    refetchInterval: 60_000,
  });

  const approve = useMutation({
    mutationFn: (r: ProductRequest) =>
      apiFetch(`/platform/tenants/${r.tenant_id}/products/${r.product}/approve`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'product-requests'] }),
  });
  const reject = useMutation({
    mutationFn: (args: { r: ProductRequest; reason: string }) =>
      apiFetch(`/platform/tenants/${args.r.tenant_id}/products/${args.r.product}/reject`, {
        method: 'POST',
        body: { reason: args.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'product-requests'] }),
  });

  const requests = data?.requests ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Hourglass className="w-5 h-5 text-amber-600" /> คำขอใช้งานแอป ({requests.length})
        </h1>
        <p className="text-sm text-gray-500">
          อนุมัติแล้วลูกค้าจะได้ทดลองใช้ฟรี 14 วัน — ครบกำหนดระบบจะตัดสิทธิ์อัตโนมัติจนกว่าจะกด “เปิดถาวร”
        </p>

        {isLoading ? (
          <p className="text-center text-gray-500 py-8">กำลังโหลด…</p>
        ) : requests.length === 0 ? (
          <p className="text-center text-gray-500 py-8">ยังไม่มีคำขอที่รออนุมัติ</p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold">
                      {PRODUCT_LABEL[r.product] ?? r.product}
                      <span className="text-gray-400 font-normal"> · </span>
                      <Link to={`/tenants/${r.tenant_id}`} className="text-brand-700 hover:underline">
                        {r.company_name}
                      </Link>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      <span className="font-mono">{r.tenant_code}</span>
                      {r.requested_at && (
                        <> · ขอเมื่อ {new Date(r.requested_at).toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</>
                      )}
                    </div>
                    {r.request_note && <div className="text-xs text-gray-600 mt-1 italic">“{r.request_note}”</div>}
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => approve.mutate(r)}
                    disabled={approve.isPending}
                    className="btn-primary text-sm flex-1"
                  >
                    <CheckCircle2 className="w-4 h-4" /> อนุมัติ · เริ่มทดลอง 14 วัน
                  </button>
                  <button
                    onClick={() => {
                      const reason = prompt('เหตุผลที่ปฏิเสธ (ไม่บังคับ)') ?? '';
                      reject.mutate({ r, reason });
                    }}
                    disabled={reject.isPending}
                    className="btn-secondary text-sm text-red-700 border-red-200"
                  >
                    <XCircle className="w-4 h-4" /> ปฏิเสธ
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
